import { ChordQuality } from '../../../types';
import { CadenceType } from '../StructureEngine';

/**
 * CadentialVoicing
 * P3 FIX: Proper cadential voicings for phrase endings
 * 
 * Implements:
 * - Cadential 6/4 inversions (I6/4 -> V -> I)
 * - Bass motion patterns for each cadence type
 * - Root position tonic enforcement at final resolution
 * - Suspension-resolution patterns
 */
export class CadentialVoicing {
    private lastBassNote: number | null = null;
    private lastVoicing: number[] = [];

    constructor() {
        this.reset();
    }

    public reset(): void {
        this.lastBassNote = null;
        this.lastVoicing = [];
    }

    /**
     * Get cadential voicing based on cadence type
     * Returns modified chord notes for proper cadential voice leading
     */
    public getCadentialVoicing(
        chordNotes: number[],
        rootMidi: number,
        quality: ChordQuality,
        cadenceType: CadenceType,
        touchesRemaining: number
    ): {
        voicedNotes: number[];
        bassNote: number;
        isRootPosition: boolean;
    } {
        if (cadenceType === 'none' || chordNotes.length === 0) {
            return {
                voicedNotes: chordNotes,
                bassNote: this.findLowestOctave(rootMidi),
                isRootPosition: true
            };
        }

        switch (cadenceType) {
            case 'perfect':
                return this.getPerfectCadenceVoicing(chordNotes, rootMidi, quality, touchesRemaining);
            case 'half':
                return this.getHalfCadenceVoicing(chordNotes, rootMidi, quality);
            case 'plagal':
                return this.getPlagalCadenceVoicing(chordNotes, rootMidi, quality);
            case 'deceptive':
                return this.getDeceptiveCadenceVoicing(chordNotes, rootMidi, quality);
            case 'imperfect':
                return this.getImperfectCadenceVoicing(chordNotes, rootMidi, quality);
            default:
                return {
                    voicedNotes: chordNotes,
                    bassNote: this.findLowestOctave(rootMidi),
                    isRootPosition: true
                };
        }
    }

    /**
     * Perfect Authentic Cadence: V -> I with root in bass and soprano
     * The strongest resolution - root position tonic with root in highest voice
     */
    private getPerfectCadenceVoicing(
        chordNotes: number[],
        rootMidi: number,
        quality: ChordQuality,
        touchesRemaining: number
    ): { voicedNotes: number[]; bassNote: number; isRootPosition: boolean } {
        // For the FINAL chord (touchesRemaining <= 1), ensure root position
        if (touchesRemaining <= 1) {
            // Force root position: root in bass, root or 5th in soprano
            const sortedNotes = [...chordNotes].sort((a, b) => a - b);
            const bassNote = this.findLowestOctave(rootMidi);

            // Move root to top for strongest resolution
            const voicedNotes = this.ensureRootInSoprano(sortedNotes, rootMidi);

            this.lastVoicing = voicedNotes;
            this.lastBassNote = bassNote;

            return { voicedNotes, bassNote, isRootPosition: true };
        }

        // For V chord before I (touchesRemaining > 1), use dominant voicing
        // Dominant with 7th resolving down
        const voicedNotes = [...chordNotes];
        const bassNote = this.findLowestOctave(rootMidi);

        this.lastVoicing = voicedNotes;
        this.lastBassNote = bassNote;

        return { voicedNotes, bassNote, isRootPosition: true };
    }

    /**
     * Half Cadence: I -> V with stepwise bass motion
     * Ends on dominant, creates expectation for continuation
     */
    private getHalfCadenceVoicing(
        chordNotes: number[],
        rootMidi: number,
        quality: ChordQuality
    ): { voicedNotes: number[]; bassNote: number; isRootPosition: boolean } {
        // V chord in root position
        const bassNote = this.findLowestOctave(rootMidi);

        // Use first inversion if approaching from below
        if (this.lastBassNote !== null && this.lastBassNote < bassNote - 5) {
            // Consider first inversion for smoother voice leading
            const thirdInterval = quality === 'minor' ? 3 : 4;
            const invertedBass = bassNote + thirdInterval - 12;

            if (invertedBass >= 36 && invertedBass <= 52) {
                this.lastBassNote = invertedBass;
                return { voicedNotes: chordNotes, bassNote: invertedBass, isRootPosition: false };
            }
        }

        this.lastBassNote = bassNote;
        return { voicedNotes: chordNotes, bassNote, isRootPosition: true };
    }

    /**
     * Plagal Cadence: IV -> I ("Amen" cadence)
     * Subdominant resolving to tonic with smooth voice leading
     */
    private getPlagalCadenceVoicing(
        chordNotes: number[],
        rootMidi: number,
        quality: ChordQuality
    ): { voicedNotes: number[]; bassNote: number; isRootPosition: boolean } {
        // Tonic in root position
        const bassNote = this.findLowestOctave(rootMidi);
        const voicedNotes = [...chordNotes];

        this.lastBassNote = bassNote;
        return { voicedNotes, bassNote, isRootPosition: true };
    }

    /**
     * Deceptive Cadence: V -> vi
     * Unexpected resolution, maintains tension
     */
    private getDeceptiveCadenceVoicing(
        chordNotes: number[],
        rootMidi: number,
        quality: ChordQuality
    ): { voicedNotes: number[]; bassNote: number; isRootPosition: boolean } {
        // vi chord - first inversion often sounds better for deceptive cadence
        const thirdInterval = quality === 'minor' ? 3 : 4;
        const firstInversionBass = rootMidi + thirdInterval;
        const bassNote = this.findLowestOctave(firstInversionBass) - 12;

        // Ensure bass is in valid range
        const safeBass = Math.max(36, Math.min(52, bassNote));

        this.lastBassNote = safeBass;
        return { voicedNotes: chordNotes, bassNote: safeBass, isRootPosition: false };
    }

    /**
     * Imperfect Authentic Cadence: V -> I with 3rd or 5th in soprano
     * Weaker than perfect cadence but still resolves
     */
    private getImperfectCadenceVoicing(
        chordNotes: number[],
        rootMidi: number,
        quality: ChordQuality
    ): { voicedNotes: number[]; bassNote: number; isRootPosition: boolean } {
        const bassNote = this.findLowestOctave(rootMidi);

        // Don't force root in soprano - allow 3rd or 5th
        const voicedNotes = [...chordNotes].sort((a, b) => a - b);

        this.lastBassNote = bassNote;
        return { voicedNotes, bassNote, isRootPosition: true };
    }

    /**
     * Get Cadential 6/4 voicing (I6/4 -> V -> I)
     * Creates strong pull to dominant before resolution
     */
    public getCadential64(
        tonicNotes: number[],
        tonicRoot: number
    ): { voicedNotes: number[]; bassNote: number } {
        // 6/4 = second inversion = 5th in bass
        const fifthInterval = 7;
        const bassNote = this.findLowestOctave(tonicRoot + fifthInterval) - 12;

        // Arrange voices for proper 6/4 voicing
        const sortedNotes = [...tonicNotes].sort((a, b) => a - b);

        // Find the 5th and move it to bass
        const voicedNotes = sortedNotes.filter(n => (n - tonicRoot) % 12 !== 7);
        voicedNotes.unshift(bassNote);

        return { voicedNotes: sortedNotes, bassNote: Math.max(36, bassNote) };
    }

    /**
     * Get bass motion pattern for cadence
     * Returns the ideal bass note progression for smooth voice leading
     */
    public getBassMotionPattern(
        cadenceType: CadenceType,
        currentRootMidi: number,
        touchesInCadence: number
    ): number {
        const bass = this.findLowestOctave(currentRootMidi);

        // Default to root if no special pattern
        if (touchesInCadence <= 1) {
            return bass;
        }

        // For longer cadential patterns, create stepwise motion
        switch (cadenceType) {
            case 'perfect':
                // V -> I: 5th moves to root (down a 5th or up a 4th)
                return bass;
            case 'half':
                // I -> V: root moves to 5th (up a 5th or down a 4th)
                return bass;
            case 'plagal':
                // IV -> I: 4th moves to root (down a 4th)
                return bass;
            default:
                return bass;
        }
    }

    /**
     * Helper: Find the lowest octave of a note class that fits bass range
     */
    private findLowestOctave(midiNote: number): number {
        let bass = midiNote;
        while (bass > 52) bass -= 12;  // Get below C4
        while (bass < 36) bass += 12;  // Above C2
        return bass;
    }

    /**
     * Helper: Ensure root note is in the soprano (highest voice) for PAC
     */
    private ensureRootInSoprano(sortedNotes: number[], rootMidi: number): number[] {
        if (sortedNotes.length === 0) return sortedNotes;

        const rootPitchClass = rootMidi % 12;
        const highestNote = sortedNotes[sortedNotes.length - 1];
        const highestPitchClass = highestNote % 12;

        // If highest note is already the root, we're good
        if (highestPitchClass === rootPitchClass) {
            return sortedNotes;
        }

        // Find a root note and move it to the top
        for (let i = 0; i < sortedNotes.length; i++) {
            if (sortedNotes[i] % 12 === rootPitchClass) {
                // Move this note up an octave to become soprano
                const result = [...sortedNotes];
                result[i] = result[i] + 12;
                result.sort((a, b) => a - b);
                return result;
            }
        }

        // If no root found, add one at the top
        const newRoot = Math.floor(highestNote / 12) * 12 + rootPitchClass + 12;
        return [...sortedNotes, newRoot];
    }

    /**
     * Check if the current voicing is appropriate for the cadence
     */
    public isAppropriateForCadence(
        chordNotes: number[],
        bassNote: number,
        cadenceType: CadenceType,
        isFinalChord: boolean
    ): boolean {
        // Final chord must be root position for PAC
        if (isFinalChord && cadenceType === 'perfect') {
            // Check if bass is the root
            const bassClass = bassNote % 12;
            const lowestChordClass = Math.min(...chordNotes.map(n => n % 12));
            return bassClass === lowestChordClass;
        }

        return true;
    }
}
